/**
 * Stock Symbol to ID Mapping
 * Centralized mapping for all stock symbols and their corresponding IDs
 * Used across the application for API calls and stock identification
 */

// Stock IDs array
const STOCK_IDS = [
  12, 10, 13, 14, 15, 16, 3, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 1, 27, 28, 29, 5, 30, 4, 7, 31, 32, 2, 33, 34, 35, 36, 37, 38, 39, 40, 6,
  41, 42, 43, 44, 45, 46, 8, 47, 48, 49, 50, 51, 52, 53, 54, 56, 57, 58, 59, 60, 61, 62, 63
]

// Stock ticker symbols array
const STOCK_TICKERS = [
  'ADANIENT', 'ADANIPORTS', 'APOLLOHOSP', 'ASIANPAINT', 'AXISBANK', 'BAJAJ-AUTO', 'BAJAJFINSV', 'BAJFINANCE', 'BEL', 'BHARTIARTL',
  'CIPLA', 'COALINDIA', 'DIVISLAB', 'DRREDDY', 'EICHERMOT', 'ETERNAL', 'HCLTECH', 'HDFCBANK', 'HDFCLIFE', 'HINDALCO', 'HINDUNILVR', 'ICICIBANK',
  'INDIGO', 'INFY', 'ITC', 'JIOFIN', 'JSWSTEEL', 'KOTAKBANK', 'LT', 'M&M', 'MARUTI', 'MAXHEALTH', 'NESTLEIND', 'NTPC', 'ONGC', 'POWERGRID',
  'RELIANCE', 'SBILIFE', 'SBIN', 'SHRIRAMFIN', 'SUNPHARMA', 'TATACONSUM', 'TATASTEEL', 'TCS', 'TECHM', 'TITAN', 'TMPV', 'TRENT',
  'ULTRACEMCO', 'WIPRO', 'AUBANK', 'MARICO', 'COFORGE', 'RAINBOW', 'NUVAMA', 'ACUTAAS', 'CHOLAFIN', 'MUTHOOTFIN', 'ABCAPITAL', 'M&MFIN'
]

// Create mapping object from ticker to ID
const STOCK_SYMBOL_TO_ID = {}
STOCK_TICKERS.forEach((ticker, index) => {
  STOCK_SYMBOL_TO_ID[ticker] = STOCK_IDS[index]
})

// Create reverse mapping from ID to ticker
const STOCK_ID_TO_SYMBOL = {}
STOCK_IDS.forEach((id, index) => {
  STOCK_ID_TO_SYMBOL[id] = STOCK_TICKERS[index]
})

// Common company name variations mapping to ticker symbols
const COMPANY_NAME_TO_TICKER = {
  // Adani Group
  'Adani Enterprises': 'ADANIENT',
  'Adani Ports': 'ADANIPORTS',
  'Adani Ports and SEZ': 'ADANIPORTS',

  // Healthcare
  'Apollo Hospitals': 'APOLLOHOSP',
  'Apollo Hospital': 'APOLLOHOSP',

  // Paint & Chemicals
  'Asian Paints': 'ASIANPAINT',
  'Asian Paint': 'ASIANPAINT',

  // Banking & Financial Services
  'Axis Bank': 'AXISBANK',
  'Bajaj Auto': 'BAJAJ-AUTO',
  'Bajaj Finserv': 'BAJAJFINSV',
  'Bajaj Finance': 'BAJFINANCE',
  'Cholamandalam Investment': 'CHOLAFIN',
  'Cholamandalam': 'CHOLAFIN',
  'Shriram Finance': 'SHRIRAMFIN',
  'Shriram Finance Ltd': 'SHRIRAMFIN',
  'Muthoot Finance': 'MUTHOOTFIN',
  'Aditya Birla Capital': 'ABCAPITAL',
  'Mahindra & Mahindra Financial Services': 'M&MFIN',
  'M&M Financial': 'M&MFIN',
  'HDFC Bank': 'HDFCBANK',
  'ICICI Bank': 'ICICIBANK',
  'Kotak Mahindra Bank': 'KOTAKBANK',
  'State Bank of India': 'SBIN',
  'SBI': 'SBIN',

  // Telecom
  'Bharti Airtel': 'BHARTIARTL',
  'Airtel': 'BHARTIARTL',

  // Pharma
  'Cipla': 'CIPLA',
  'Dr Reddy': 'DRREDDY',
  'Dr Reddys': 'DRREDDY',
  'Sun Pharma': 'SUNPHARMA',
  'Sun Pharmaceutical': 'SUNPHARMA',

  // IT Services
  'HCL Tech': 'HCLTECH',
  'HCL Technologies': 'HCLTECH',
  'Infosys': 'INFY',
  'TCS': 'TCS',
  'Tata Consultancy Services': 'TCS',
  'Tech Mahindra': 'TECHM',
  'Wipro': 'WIPRO',

  // Consumer Goods
  'Hindustan Unilever': 'HINDUNILVR',
  'HUL': 'HINDUNILVR',
  'ITC': 'ITC',
  'Nestle India': 'NESTLEIND',
  'Tata Consumer': 'TATACONSUM',
  'Tata Consumer Products': 'TATACONSUM',

  // Automotive
  'Maruti Suzuki': 'MARUTI',
  'Maruti': 'MARUTI',
  'Mahindra': 'M&M',
  'M&M': 'M&M',
  'Eicher Motors': 'EICHERMOT',

  // Oil & Gas
  'Reliance': 'RELIANCE',
  'Reliance Industries': 'RELIANCE',
  'ONGC': 'ONGC',
  'Oil and Natural Gas Corporation': 'ONGC',

  // Metals & Mining
  'Tata Steel': 'TATASTEEL',
  'JSW Steel': 'JSWSTEEL',
  'Hindalco': 'HINDALCO',
  'Coal India': 'COALINDIA',

  // Power
  'Power Grid': 'POWERGRID',
  'PowerGrid': 'POWERGRID',
  'Power Grid Corporation': 'POWERGRID',
  'NTPC': 'NTPC',

  // Others
  'Larsen & Toubro': 'LT',
  'L&T': 'LT',
  'Titan': 'TITAN',
  'Titan Company': 'TITAN',
  'UltraTech Cement': 'ULTRACEMCO',
  'UltraTech': 'ULTRACEMCO',
  'Trent': 'TRENT',
  'IndiGo': 'INDIGO',
  'InterGlobe Aviation': 'INDIGO'
}

/**
 * Get stock ID from ticker symbol
 * @param {string} ticker - Stock ticker symbol (e.g., 'RELIANCE', 'TCS')
 * @returns {number|null} - Stock ID or null if not found
 */
export function getStockIdFromTicker(ticker) {
  if (!ticker) return null

  // Convert to uppercase and trim
  const normalizedTicker = ticker.toString().toUpperCase().trim()

  return STOCK_SYMBOL_TO_ID[normalizedTicker] || null
}

/**
 * Get ticker symbol from stock ID
 * @param {number} stockId - Stock ID
 * @returns {string|null} - Ticker symbol or null if not found
 */
export function getTickerFromStockId(stockId) {
  if (!stockId) return null

  return STOCK_ID_TO_SYMBOL[stockId] || null
}

/**
 * Get stock ID from company name (with fuzzy matching)
 * @param {string} companyName - Company name or security name
 * @returns {number|null} - Stock ID or null if not found
 */
export function getStockIdFromCompanyName(companyName) {
  if (!companyName) return null

  const name = companyName.toString().trim()

  // Try exact match with company name mappings
  if (COMPANY_NAME_TO_TICKER[name]) {
    return getStockIdFromTicker(COMPANY_NAME_TO_TICKER[name])
  }

  // Try direct ticker match
  const directId = getStockIdFromTicker(name)
  if (directId) return directId

  // Try partial matching (case insensitive)
  const nameLower = name.toLowerCase()

  // Check if the name contains any of our mapped company names
  for (const [companyName, ticker] of Object.entries(COMPANY_NAME_TO_TICKER)) {
    if (companyName.toLowerCase().includes(nameLower) || nameLower.includes(companyName.toLowerCase())) {
      return getStockIdFromTicker(ticker)
    }
  }

  // Check if the name contains any ticker symbols
  for (const ticker of STOCK_TICKERS) {
    if (nameLower.includes(ticker.toLowerCase()) || ticker.toLowerCase().includes(nameLower)) {
      return getStockIdFromTicker(ticker)
    }
  }

  return null
}

/**
 * Check if a ticker symbol exists in our mappings
 * @param {string} ticker - Stock ticker symbol
 * @returns {boolean} - True if ticker exists
 */
export function isValidTicker(ticker) {
  return getStockIdFromTicker(ticker) !== null
}

/**
 * Get all available ticker symbols
 * @returns {string[]} - Array of all ticker symbols
 */
export function getAllTickers() {
  return [...STOCK_TICKERS]
}

/**
 * Get all available stock IDs
 * @returns {number[]} - Array of all stock IDs
 */
export function getAllStockIds() {
  return [...STOCK_IDS]
}

/**
 * Get the complete mapping object (ticker -> ID)
 * @returns {Object} - Complete mapping object
 */
export function getCompleteMapping() {
  return { ...STOCK_SYMBOL_TO_ID }
}

// Export constants for direct use if needed
export {
  STOCK_IDS,
  STOCK_TICKERS,
  STOCK_SYMBOL_TO_ID,
  STOCK_ID_TO_SYMBOL,
  COMPANY_NAME_TO_TICKER
}
